import { create } from "zustand";
import { send_request } from "../../../utils/Request";
import {
  extractApiErrorMessage,
  isFailedRequest,
} from "../../../utils/apiErrorMessage";

const TERMINAL_STATUSES = ["COMPLETE", "STOPPED", "ERROR"];

export function isMissionActive(mission) {
  return Boolean(mission) && !TERMINAL_STATUSES.includes(mission.status);
}

/**
 * Per-session harness controls behind the chat-header "make this a mission" UI.
 * State is keyed by chatSessionId so several open conversations never collide.
 */
const useMissionStore = create((set, get) => ({
  missionBySession: {},
  loadingBySession: {},
  errorBySession: {},

  // ── Primary (top-level) missions: /missions list + Mission Home ──
  primaryMissions: [],
  primaryLoading: false,
  primaryError: null,
  primaryDetailById: {},

  fetchPrimaryMissions: async () => {
    set({ primaryLoading: true, primaryError: null });
    const response = await send_request(
      "project-service/agent/missions",
      "",
      {},
      "GET"
    );
    if (isFailedRequest(response)) {
      set({
        primaryLoading: false,
        primaryError: extractApiErrorMessage(response, "Failed to load missions"),
      });
      return get().primaryMissions;
    }
    const missions = Array.isArray(response?.data) ? response.data : [];
    set({ primaryMissions: missions, primaryLoading: false });
    return missions;
  },

  fetchPrimaryMission: async (missionId) => {
    if (!missionId) return null;
    set({ primaryLoading: true, primaryError: null });
    const response = await send_request(
      `project-service/agent/missions/${missionId}`,
      "",
      {},
      "GET"
    );
    if (isFailedRequest(response)) {
      // Keep the last-known detail so a transient poll failure never flips the UI
      // into a "not found" state (Request.js resolves failures, it does not throw).
      set({
        primaryLoading: false,
        primaryError: extractApiErrorMessage(response, "Failed to load mission"),
      });
      return get().primaryDetailById[missionId] || null;
    }
    const detail = response?.data || null;
    set((s) => ({
      primaryLoading: false,
      primaryDetailById: { ...s.primaryDetailById, [missionId]: detail },
    }));
    return detail;
  },

  createPrimaryMission: async (payload) => {
    set({ primaryError: null });
    const response = await send_request(
      "project-service/agent/missions",
      payload || {},
      {},
      "POST"
    );
    if (isFailedRequest(response)) {
      const message = extractApiErrorMessage(response, "Failed to create mission");
      set({ primaryError: message });
      throw new Error(message);
    }
    return response?.data || null;
  },

  // Shared envelope for primary-mission mutations: POST, then re-fetch detail so status,
  // activity and resources reflect the new state. Returns {ok, detail} / {ok:false, error}.
  _postAndRefetch: async (missionId, path, body, failMessage) => {
    if (!missionId) {
      return { ok: false, error: "Missing mission" };
    }
    const response = await send_request(path, body || {}, {}, "POST");
    if (isFailedRequest(response)) {
      return { ok: false, error: extractApiErrorMessage(response, failMessage) };
    }
    const detail = await get().fetchPrimaryMission(missionId);
    return { ok: true, detail };
  },

  // Primary-mission lifecycle (Mission Home header).
  primaryLifecycle: (missionId, action) =>
    get()._postAndRefetch(
      missionId,
      `project-service/agent/mission/${missionId}/${action}`,
      {},
      `Failed to ${action} mission`
    ),

  // Records an external event and schedules an EVENT wake so the orchestrator reviews it now
  // (resumes the mission if it was WAITING on input).
  wakeMission: (missionId, summary) =>
    get()._postAndRefetch(
      missionId,
      `project-service/agent/mission/${missionId}/event-wake`,
      { source: "USER_MESSAGE", summary },
      "Failed to wake mission"
    ),

  updateMissionPlan: (missionId, plan) =>
    get()._postAndRefetch(
      missionId,
      `project-service/agent/mission/${missionId}/plan`,
      { plan: plan || {} },
      "Failed to save plan"
    ),

  updateMissionConfig: (missionId, payload) =>
    get()._postAndRefetch(
      missionId,
      `project-service/agent/mission/${missionId}/config`,
      payload || {},
      "Failed to update configuration"
    ),

  updateMissionAcceptanceCriteria: (missionId, completed) =>
    get()._postAndRefetch(
      missionId,
      `project-service/agent/mission/${missionId}/acceptance-criteria`,
      { completed: completed || [] },
      "Failed to update acceptance criteria"
    ),

  // Teardown (Phase 5): on a completed PRIMARY mission, owned resources can be promoted to
  // standalone or unlinked. Linked resources were already auto-unlinked when the mission finalized.
  fetchMissionTeardown: async (missionId) => {
    if (!missionId) return null;
    const response = await send_request(
      `project-service/agent/mission/${missionId}/teardown`,
      "",
      {},
      "GET"
    );
    if (isFailedRequest(response)) {
      set({
        primaryError: extractApiErrorMessage(
          response,
          "Failed to load teardown plan"
        ),
      });
      return null;
    }
    return response?.data || null;
  },

  applyMissionTeardown: async (missionId, decisions) => {
    if (!missionId) return null;
    const response = await send_request(
      `project-service/agent/mission/${missionId}/teardown`,
      { decisions: decisions || [] },
      {},
      "POST"
    );
    if (isFailedRequest(response)) {
      set({
        primaryError: extractApiErrorMessage(response, "Failed to apply teardown"),
      });
      return null;
    }
    return response?.data || null;
  },

  fetchMission: async (chatSessionId) => {
    if (!chatSessionId) return null;
    set((s) => ({
      loadingBySession: { ...s.loadingBySession, [chatSessionId]: true },
    }));
    const response = await send_request(
      `project-service/agent/session/${chatSessionId}/mission`,
      "",
      {},
      "GET"
    );
    if (isFailedRequest(response)) {
      set((s) => ({
        loadingBySession: { ...s.loadingBySession, [chatSessionId]: false },
        errorBySession: {
          ...s.errorBySession,
          [chatSessionId]: extractApiErrorMessage(
            response,
            "Failed to load mission"
          ),
        },
      }));
      return get().missionBySession[chatSessionId] || null;
    }
    const mission = response?.data || null;
    set((s) => ({
      missionBySession: { ...s.missionBySession, [chatSessionId]: mission },
      loadingBySession: { ...s.loadingBySession, [chatSessionId]: false },
    }));
    return mission;
  },

  startMission: async (
    chatSessionId,
    { objective, name, templateId, policy } = {}
  ) => {
    if (!chatSessionId) return null;
    const response = await send_request(
      `project-service/agent/session/${chatSessionId}/mission/start`,
      { objective, name, templateId, policy },
      {},
      "POST"
    );
    if (isFailedRequest(response)) {
      set((s) => ({
        errorBySession: {
          ...s.errorBySession,
          [chatSessionId]: extractApiErrorMessage(
            response,
            "Failed to start mission"
          ),
        },
      }));
      return null;
    }
    const mission = response?.data || null;
    if (mission) {
      set((s) => ({
        missionBySession: { ...s.missionBySession, [chatSessionId]: mission },
      }));
    }
    return mission;
  },

  _lifecycle: async (chatSessionId, missionId, action) => {
    if (!missionId) return null;
    const response = await send_request(
      `project-service/agent/mission/${missionId}/${action}`,
      {},
      {},
      "POST"
    );
    if (isFailedRequest(response)) {
      set((s) => ({
        errorBySession: {
          ...s.errorBySession,
          [chatSessionId]: extractApiErrorMessage(
            response,
            `Failed to ${action} mission`
          ),
        },
      }));
      return null;
    }
    const mission = response?.data || null;
    if (mission) {
      set((s) => ({
        missionBySession: { ...s.missionBySession, [chatSessionId]: mission },
      }));
    }
    return mission;
  },

  pauseMission: (chatSessionId, missionId) =>
    get()._lifecycle(chatSessionId, missionId, "pause"),
  resumeMission: (chatSessionId, missionId) =>
    get()._lifecycle(chatSessionId, missionId, "resume"),
  stopMission: (chatSessionId, missionId) =>
    get()._lifecycle(chatSessionId, missionId, "stop"),
}));

export default useMissionStore;
